import { Button, Grid, TextField, MenuItem, DialogActions } from '@mui/material';
import React, { useState, useEffect, useContext } from 'react';
import { AlertMessagesContext } from 'react-alert-messages';
import styles from './styles.module.css';
import CloseIcon from '@mui/icons-material/Close';
import NumberField from 'components/library/NumberField';
import { useTranslation } from 'react-i18next';
import AccountServices from 'services/AccountServices';

export default function CashInOutDialog({ defaultAccountHeadId, onSuccess, onClose }) {
  const { t: tCommon } = useTranslation('common');

  const [accountHeads, setAccountHeads] = useState([]);
  const [selectedHeadId, setSelectedHeadId] = useState(defaultAccountHeadId || '');
  const [toHeadId, setToHeadId] = useState('');
  const [type, setType] = useState('in'); // 'in', 'out', or 'transfer'
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [loadingIndicator, setLoadingIndicator] = useState(false);

  const { postAlertMessage } = useContext(AlertMessagesContext);

  useEffect(() => {
    const fetchHeads = async () => {
      try {
        const data = await AccountServices.getAccountHeads();
        const heads = data.results || data || [];
        setAccountHeads(heads);
        
        if (!selectedHeadId && heads.length > 0) {
          const cashHead = heads.find((h) => h.name.toLowerCase() === 'cash');
          if (cashHead) {
            setSelectedHeadId(cashHead.id);
          } else {
            setSelectedHeadId(heads[0].id);
          }
        }
      } catch (err) {
        console.error('Failed to load account heads', err);
      }
    };
    fetchHeads();
  }, [selectedHeadId]);

  const handleSubmit = async () => {
    if (!amount || Number(amount) <= 0) {
      postAlertMessage({
        text: tCommon('Please enter a valid amount'),
        type: 'failed',
      });
      return;
    }

    let payload = {};

    if (type === 'transfer') {
      if (!selectedHeadId || !toHeadId) {
        postAlertMessage({
          text: tCommon('Please select both Source and Destination accounts'),
          type: 'failed',
        });
        return;
      }
      if (selectedHeadId === toHeadId) {
        postAlertMessage({
          text: tCommon('Source and Destination accounts must be different'),
          type: 'failed',
        });
        return;
      }

      const fromName = accountHeads.find((h) => h.id === selectedHeadId)?.name || '';
      const toName = accountHeads.find((h) => h.id === toHeadId)?.name || '';
      const autoDesc = tCommon('Balance Transfer from {{from}} to {{to}}', { from: fromName, to: toName });
      const finalDescription = description.trim() ? `${autoDesc} - ${description.trim()}` : autoDesc;

      payload = {
        from_account_id: selectedHeadId,
        to_account_id: toHeadId,
        amount: Number(amount),
        type: 'transfer',
        description: finalDescription,
        date,
      };
    } else {
      if (!selectedHeadId) {
        postAlertMessage({
          text: tCommon('Please select a valid account head'),
          type: 'failed',
        });
        return;
      }

      const accountName = accountHeads.find((h) => h.id === selectedHeadId)?.name || '';
      const autoDesc = type === 'in' 
        ? tCommon('Deposit to {{account}}', { account: accountName }) 
        : tCommon('Withdrawal from {{account}}', { account: accountName });
      const finalDescription = description.trim() ? `${autoDesc} - ${description.trim()}` : autoDesc;

      payload = {
        account_head_id: selectedHeadId,
        amount: Number(amount),
        type,
        description: finalDescription,
        date,
      };
    }

    setLoadingIndicator(true);
    try {
      await AccountServices.createLedgerEntry(payload);
      postAlertMessage({
        text: tCommon('Transaction recorded successfully'),
        type: 'success',
      });
      onSuccess();
    } catch (err) {
      postAlertMessage({
        text: err.message || tCommon('Failed to record transaction'),
        type: 'failed',
      });
    }
    setLoadingIndicator(false);
  };

  const getHelperText = (headId, isFromAccount) => {
    const head = accountHeads.find((h) => h.id === headId);
    if (!head) return '';
    const currentBalance = Number(head.balance || 0);
    const numAmount = Number(amount || 0);
    let updatedBalance = currentBalance;
    
    if (type === 'transfer') {
      if (isFromAccount) {
        updatedBalance = currentBalance - numAmount;
      } else {
        updatedBalance = currentBalance + numAmount;
      }
    } else if (type === 'in') {
      updatedBalance = currentBalance + numAmount;
    } else if (type === 'out') {
      updatedBalance = currentBalance - numAmount;
    }

    return `${tCommon('Current')}: ${currentBalance.toFixed(2)}  |  ${tCommon('Updated')}: ${updatedBalance.toFixed(2)}`;
  };

  return (
    <div className={styles.popUp} style={{ zIndex: 1300 }}>
      <div className={styles.contentWrapper}>
        <div className={styles.headSec}>
          <h2 className={styles.editTitle}>
            {type === 'transfer' ? tCommon('Account Balance Transfer') : tCommon('Deposit / Withdrawal')}
          </h2>
          <CloseIcon onClick={onClose} style={{ cursor: 'pointer' }} />
        </div>
        <div className={styles.inputSec}>
          <Grid container className={styles.inputRow} alignItems="top" spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label={tCommon('Transaction Type')}
                variant="outlined"
                size="small"
                value={type}
                onChange={(e) => setType(e.target.value)}
                className={styles.selectBox}
                fullWidth
              >
                <MenuItem value="in">{tCommon('Deposit')}</MenuItem>
                <MenuItem value="out">{tCommon('Withdrawal')}</MenuItem>
                <MenuItem value="transfer">{tCommon('Balance Transfer')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberField
                label={tCommon('Amount')}
                className={styles.numberInput}
                name="amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={type === 'transfer' ? 6 : 12}>
              <TextField
                select
                label={type === 'transfer' ? tCommon('From Account') : tCommon('Account Head')}
                variant="outlined"
                size="small"
                value={selectedHeadId}
                onChange={(e) => setSelectedHeadId(e.target.value)}
                className={styles.selectBox}
                fullWidth
                helperText={selectedHeadId ? getHelperText(selectedHeadId, true) : ''}
                FormHelperTextProps={{ style: { fontWeight: 500, color: '#00a65a' } }}
              >
                {accountHeads.map((head) => (
                  <MenuItem key={head.id} value={head.id}>
                    {head.name} ({head.type})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {type === 'transfer' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label={tCommon('To Account')}
                  variant="outlined"
                  size="small"
                  value={toHeadId}
                  onChange={(e) => setToHeadId(e.target.value)}
                  className={styles.selectBox}
                  fullWidth
                  helperText={toHeadId ? getHelperText(toHeadId, false) : ''}
                  FormHelperTextProps={{ style: { fontWeight: 500, color: '#00a65a' } }}
                >
                  {accountHeads.map((head) => (
                    <MenuItem key={head.id} value={head.id} disabled={head.id === selectedHeadId}>
                      {head.name} ({head.type})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={tCommon('Transaction Date')}
                    type="date"
                    variant="outlined"
                    size="small"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={tCommon('Description')}
                    placeholder={
                      type === 'in'
                        ? tCommon('e.g. Owner Investment, Cash Deposit')
                        : type === 'out'
                        ? tCommon('e.g. Personal Drawings, Petty Cash Draw')
                        : tCommon('e.g. Cash collection deposit to bank/UPI')
                    }
                    variant="outlined"
                    size="small"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </div>
        <div className={styles.footSec}>
          <DialogActions>
            <Button variant="contained" color="secondary" size="small" onClick={onClose}>
              {tCommon('Close')}
            </Button>
            <Button
              variant="contained"
              size="small"
              color="primary"
              style={{ backgroundColor: '#00a65a' }}
              disabled={amount === '' || selectedHeadId === '' || (type === 'transfer' && toHeadId === '') || loadingIndicator}
              onClick={handleSubmit}
            >
              {tCommon('Save Transaction')}
            </Button>
          </DialogActions>
        </div>
      </div>
    </div>
  );
}
